import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import OmnichannelChatWarningDialog from '../../modals/OmnichannelChatWarningDialog';

/**
 * AddProductSection component for adding products to the calculator
 * @param {Object} props - Component props
 * @param {Array} props.productOptions - Array of available products
 * @param {Function} props.isProductSelected - Function to check if a product is selected
 * @param {Function} props.toggleProduct - Function to toggle product selection
 * @param {string} props.packageTier - Current package tier ('basic', 'pro', 'corporate')
 */
const AddProductSection = ({
  productOptions,
  isProductSelected,
  toggleProduct,
  packageTier = 'basic' // Default to basic tier when not provided
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [showOmnichannelWarning, setShowOmnichannelWarning] = useState(false);

  // Products that are included in tier (shown with green highlighting)
  const getIncludedProducts = (tier) => {
    // Corporate includes Eniq AI Standard instead of AI Conversation Intelligence
    if (tier === 'corporate') {
      return ['callback', 'omnichannel-chat', 'eniq-ai'];
    }
    if (tier === 'pro') {
      return ['ai-conversation', 'callback', 'omnichannel-chat'];
    }
    if (tier === 'advanced') {
      return ['callback'];
    }
    return [];
  };

  const includedProducts = getIncludedProducts(packageTier);

  // Check if a product is included in the current tier
  const isIncludedInTier = (productId) => includedProducts.includes(productId);

  const handleProductToggle = (productId) => {
    // Check if trying to add Omnichannel Chat without Business Phone
    if (productId === 'omnichannel-chat' && !isProductSelected('business-phone')) {
      setShowOmnichannelWarning(true);
      return;
    }
    toggleProduct(productId);
  };

  return (
    <>
    <Paper 
      elevation={0} 
      sx={{
        p: 3, 
        mb: 4,
        borderRadius: theme.spacing(1), 
        border: '1px solid rgba(0, 0, 0, 0.12)', 
        boxShadow: 'none', 
        '&:hover': {
          boxShadow: theme.shadows[2], 
        },
      }}
    >
      <Typography sx={{ mb: 3, fontWeight: 500, fontSize: 17, color: '#333' }}>
        {t('calculator.addProducts', 'Add Products')}
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {/* Filter out already selected products - show included products with highlighting */}
        {productOptions
          .filter(product => !isProductSelected(product.id))
          .length === 0 && productOptions.length > 0 && (
          <Box sx={{
            p: 2,
            textAlign: 'center',
            bgcolor: 'rgba(0, 200, 83, 0.05)',
            borderRadius: theme.spacing(1),
            border: '1px dashed #00C853',
            width: '100%'
          }}>
            <Typography sx={{ color: '#00A040', fontSize: 14, fontWeight: 500 }}>
              {t('calculator.allProductsAdded', 'All available products have been added')}
            </Typography>
          </Box>
        )}

        {productOptions
          .filter(product => !isProductSelected(product.id))
          .map((product) => {
            const isIncluded = isIncludedInTier(product.id);
            const ProductIcon = product.icon;

            const chipProps = {
              icon: ProductIcon ? <ProductIcon sx={{ color: '#00C853', fontSize: 18 }} /> : null,
              label: product.label,
              onClick: () => handleProductToggle(product.id),
              deleteIcon: isIncluded
                ? <CheckCircleIcon sx={{ color: '#00C853', fontSize: 16 }} />
                : <AddIcon sx={{ color: '#00C853', fontSize: 16 }} />,
              onDelete: () => handleProductToggle(product.id),
              sx: {
                borderRadius: theme.spacing(1),
                py: 1,
                px: 0.8,
                height: 'auto',
                boxShadow: 'none',
                transition: 'box-shadow 0.2s, background-color 0.2s, border-color 0.2s',
                bgcolor: isIncluded ? 'rgba(0, 200, 83, 0.08)' : 'white',
                border: isIncluded ? '1px solid #00C853' : '1px solid rgba(0, 0, 0, 0.12)',
                '& .MuiChip-label': {
                  fontSize: 14,
                  px: 1,
                  py: 0.5,
                  fontWeight: 500,
                  mr: 0.6,
                  color: isIncluded ? '#00A040' : 'inherit',
                },
                '&:hover': {
                  bgcolor: isIncluded ? 'rgba(0, 200, 83, 0.15)' : 'white',
                  borderColor: isIncluded ? '#00C853' : 'rgba(0, 0, 0, 0.12)',
                  boxShadow: theme.shadows[2],
                },
                '& .MuiChip-deleteIcon': {
                  color: '#00C853',
                  bgcolor: isIncluded ? 'rgba(0, 200, 83, 0.2)' : 'rgba(0, 200, 83, 0.1)',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  '&:hover': { bgcolor: 'rgba(0, 200, 83, 0.3)' }
                }
              }
            };

            // Wrap included products with tooltip
            if (isIncluded) {
              return (
                <Tooltip
                  key={product.id}
                  title={t('calculator.includedInTier', 'Included in your {{tier}} plan', { tier: packageTier.charAt(0).toUpperCase() + packageTier.slice(1) })}
                  arrow
                  placement="top"
                >
                  <Chip {...chipProps} />
                </Tooltip>
              );
            }

            return <Chip key={product.id} {...chipProps} />;
          })}
      </Box>
    </Paper>
    
    {/* Omnichannel Chat Warning Dialog */}
    <OmnichannelChatWarningDialog 
      open={showOmnichannelWarning}
      onClose={() => setShowOmnichannelWarning(false)}
    />
    </>
  );
};

export default AddProductSection;
